import { Inject } from '@odinjs/odin';

import Controller, { OnBeforeCancel, OnBeforeSubmit, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EnteralNutritionInterruptAliases, FragmentNames } from '../../../../../codegen';
import BaseController from '../../../../BaseController';
import { EnteralNutritionType, EventType, GenerateChangePrescribedSolutionResponse, ReasonInterrupt } from '../enteral-nutrition-type';
import { AutocompleteAPI, FieldAPI, FormAPI, InputAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../../constants/endpoints';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import moment from 'moment';
import { AdminModalsService } from '../../../services/admin-modals-service';
import { ApplicationRoute } from '../../../../../constants/routes';
import MedicationService from '../../../../../controllers/adep/services/medication-service';


@Controller({ component: EnteralNutritionInterruptAliases.INTERRUPT_ENTERAL_NUTRITION, fragment: FragmentNames.ENTERAL_NUTRITION_INTERRUPT })
export default class EnteralNutritionInterruptController extends BaseController {

  @Inject(EnteralNutritionInterruptAliases.TITLE_FIELD)
  private titleFieldAPI!: FieldAPI;

  @Inject(EnteralNutritionInterruptAliases.INTERRUPT_FORM)
  private interruptFormAPI!: FormAPI;

  @Inject(EnteralNutritionInterruptAliases.REASON_AUTOCOMPLETE)
  private reasonAutocompleteAPI!: AutocompleteAPI;

  @Inject(EnteralNutritionInterruptAliases.PARTIAL_VOLUME_INPUT)
  private partialVolumeInputAPI!: InputAPI;

  @Inject(EnteralNutritionInterruptAliases.DISCARDED_VOLUME_INPUT)
  private discardedVolumeInputAPI!: InputAPI;

  @Inject(EnteralNutritionInterruptAliases.INFUSED_VOLUME_INPUT)
  private infusedVolumeInputAPI!: InputAPI;

  @Inject(EnteralNutritionInterruptAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaAPI!: TextAreaAPI;

  @OnBeforeCancel(EnteralNutritionInterruptAliases.INTERRUPT_FORM)
  cancelInterruptSolutionButtonClick(): void {
    applicationContext.popNavigationItem();
  }

  @OnMount({ target: EnteralNutritionInterruptAliases.INTERRUPT_ENTERAL_NUTRITION })
  handleLayoutInterruptMount(): void {
    const eventType = EventType.INTERRUPTION_OF_SOLUTIONS;
    const discardedVolume = 0;
    const { enteralNutrition } = this.context.getBulkData() as { enteralNutrition: EnteralNutritionType }
    this.context.set({  enteralNutrition, eventType });
    this.titleFieldAPI.setData({  materialDescription: enteralNutrition.materialDescription });
    this.interruptFormAPI.setData({
      ...enteralNutrition,
      discardedVolume,
    });
    this.interruptFormAPI.setLoading(false);
  }

  @OnBeforeSubmit({ target: EnteralNutritionInterruptAliases.INTERRUPT_FORM })
  handleBeforeSubmit(): void {
    this.generatePresciptionChangesInterrupt();
  }

  private generatePresciptionChangesInterrupt(): void {
    const enteralNutrition = this.context.get('enteralNutrition') as EnteralNutritionType;
    const { currentTime } = this.context.getBulkData() as { currentTime: string };
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const reasonSelected = this.reasonAutocompleteAPI.getValue() as unknown as ReasonInterrupt;
    const emarInterruptReasonSequence = reasonSelected ? reasonSelected.emarInterruptReasonSequence : 0;

    const body = {
      changeDate: currentTime,
      dateTime: null,
      despisedVolumeQuantity: this.discardedVolumeInputAPI.getValue() as number,
      dosageType: null,
      encounterNumber: encounterId as number,
      infusedVolumeQuantity: this.infusedVolumeInputAPI.getValue() as number,
      infusionVelocityQuantity: enteralNutrition.infusionSpeed as unknown as number ?? 0,
      justificationDescription: this.notesJustificationTextAreaAPI.getValue() as string,
      partialVolumeQuantity: this.partialVolumeInputAPI.getValue() as number,
      prescriptionNumber: enteralNutrition.prescriptionNumber,
      reasonSequenceNumber: emarInterruptReasonSequence,
      solutionSequenceNumber: enteralNutrition.materialSequence,
      volumePhaseQuantity: this.infusedVolumeInputAPI.getValue() as number,
    }

    HttpService.post(
      Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR,
      {},
      { body }
    ).then(response => {
      const responseBody = response.body as GenerateChangePrescribedSolutionResponse;
      if(responseBody) {
        this.showResponseMessage(responseBody?.messageDescription);
        this.redirectToTimeline();
      }
    }).catch((error) => {
      if (error.body && error.body.errors) {
        error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
      }
    })
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('tws.emar.interrupt-solution-success'), ToastLevel.SUCCESS);
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.ENTERAL_NUTRITION_INTERRUPT);
    AdminModalsService.redirectToTimeline(FragmentNames.ENTERAL_NUTRITION_VIEWER);
  }

  @OnMount({ target: EnteralNutritionInterruptAliases.DATE_INPUT })
  handleDateFieldMount(event: MountEvent): void {
    const api = event.target as InputAPI;
    const currentTimeMoment = moment();
    this.context.set({ currentTime: currentTimeMoment.format(ISO_FULL_DATE_FORMAT)});
    api.setValue(currentTimeMoment.utc().format(ISO_FULL_DATE_FORMAT));
  }

  @OnChange(EnteralNutritionInterruptAliases.INFUSED_VOLUME_INPUT)
  handleInfusedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.discardedVolumeInputAPI);
  }

  @OnChange(EnteralNutritionInterruptAliases.DISCARDED_VOLUME_INPUT)
  handleDiscardedVolumeInputChange(event: ChangeEvent<string>): void {
    this.handleVolumeInputChange(event, this.infusedVolumeInputAPI);
  }

  private isValidChar(value: string, oldValue?: string): boolean {
    return value === '.' || value === ',' || (value === '' && oldValue !== '')
  }

  private handleVolumeInputChange({ value, oldValue }: ChangeEvent<string>, input: InputAPI): void {
    const actualValue = Number(value) || 0;
    if(this.isValidChar(value, oldValue) || isNaN(actualValue) || oldValue === value) {
      return
    }
    const { stageVolume } = this.context.get('enteralNutrition');
    const updatedVolume = this.calculateVolumeSolution(actualValue, stageVolume);
    input.setValue(updatedVolume);
  }

  private calculateVolumeSolution(infusedVolume: number,stageVolume: number): number {
    const defaultValue = 0;
    if (stageVolume > defaultValue) {
      const result = stageVolume - infusedVolume;
      return Math.max(defaultValue, parseFloat(result.toFixed(2)));
    }

    return defaultValue;
  }
}
